import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import PublishIcon from "@mui/icons-material/Publish";
import ReplayIcon from "@mui/icons-material/Replay";
import UnpublishedIcon from "@mui/icons-material/Unpublished";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { Box, IconButton, Tooltip } from "@mui/material";
import {
  canEditStory,
  canPublishStory,
  canUnpublishStory,
} from "Components/Stories/Management/storyActionRules";
import { type StoryListItemDto, StoryState } from "Infrastructure/Api/Api";
import { Link } from "Navigation/Link";
import { Resources, useResource } from "Translations/Resources";
import { appUrl } from "Utils/UrlUtils";

const RESOURCES = Resources.Stories.Management;

type Props = {
  story: StoryListItemDto;
  isPending: boolean;
  /** Omitted where the story media itself already opens the preview (the Merchant cards). */
  onPreview?: (story: StoryListItemDto) => void;
  onPublish: (story: StoryListItemDto) => void;
  onUnpublish: (story: StoryListItemDto) => void;
  onDelete: (story: StoryListItemDto) => void;
};

/** The per-story actions shared by the Admin table and the Merchant cards; availability follows the derived state. */
export const StoryActionButtons = ({
  story,
  isPending,
  onPreview,
  onPublish,
  onUnpublish,
  onDelete,
}: Props) => {
  const { t } = useResource();
  const isEditable = canEditStory(story.state);
  const isPublishable = canPublishStory(story.state);
  const isUnpublishable = canUnpublishStory(story.state);
  const publishLabel = t(
    story.state === StoryState.Draft
      ? RESOURCES.Table.Publish
      : RESOURCES.Table.Republish,
  );

  return (
    <Box display="flex" gap={0.5} flexWrap="wrap">
      {onPreview && (
        <Tooltip title={t(RESOURCES.Table.Preview)}>
          <IconButton size="small" onClick={() => onPreview(story)}>
            <VisibilityIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
      <Tooltip
        title={
          isEditable
            ? t(Resources.Common.Edit)
            : t(RESOURCES.Messages.LockedWhileLive)
        }
      >
        {/* A span keeps the tooltip working on the disabled button. */}
        <span>
          <IconButton
            component={Link}
            to={`${appUrl("admin-stories-edit")}/${story.publicID}/edit`}
            size="small"
            color="primary"
            disabled={!isEditable}
          >
            <EditIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
      {/* Publish and unpublish are mutually exclusive (not live vs. live), so only one is ever shown. */}
      {isPublishable && (
        <Tooltip title={publishLabel}>
          <span>
            <IconButton
              size="small"
              color="success"
              disabled={isPending}
              onClick={() => onPublish(story)}
            >
              {story.state === StoryState.Draft ? (
                <PublishIcon fontSize="small" />
              ) : (
                <ReplayIcon fontSize="small" />
              )}
            </IconButton>
          </span>
        </Tooltip>
      )}
      {isUnpublishable && (
        <Tooltip title={t(RESOURCES.Table.Unpublish)}>
          <span>
            <IconButton
              size="small"
              color="warning"
              disabled={isPending}
              onClick={() => onUnpublish(story)}
            >
              <UnpublishedIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      )}
      <Tooltip title={t(Resources.Common.Delete)}>
        <span>
          <IconButton
            size="small"
            color="error"
            disabled={isPending}
            onClick={() => onDelete(story)}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
    </Box>
  );
};
